/* eslint-disable no-magic-numbers, max-params, no-bitwise, max-lines */

import colorOkLab from "./color_oklab.js"

const {
    convertRGBToOkLab,
    convertRGBToOkHSL,
    convertRGBToOkHSV,
    convertOkLabToRGB,
    convertOkHSLToRGB,
    convertOkHSVToRGB,
    distance,
    deltaE2000
} = colorOkLab


// Colors here are encoded in RGB values between 0 and 1
// These are not linear RGB values but gamma corrected ones
// Gamma corrected space is compliant with pictures and sRGB
// HSL conversion is in gamma corrected space too


function Color (polyParam, green, blue, opacity = 1) {
    const type = typeof polyParam
    if (type === "number") {
        this.rgbValues = {
            red: polyParam,
            green,
            blue
        }

        this.opacity = opacity
        this.perceptual = true
    } else if (type === "object") {
        createFromObject(this, polyParam)
    } else {
        cssCreate(this, polyParam)
    }
}


function createFromObject (color, polyParam) {
    const {opacity = 1, perceptual = true} = polyParam
    color.opacity    = opacity
    color.perceptual = perceptual
    if ("red" in polyParam) {
        color.rgbValues = {
            red:   polyParam.red,
            green: polyParam.green,
            blue:  polyParam.blue
        }
    } else if ("hue" in polyParam) {
        hslCreate(color, polyParam)
    } else if ("light" in polyParam) {
        lightCreate(color, polyParam.light)
    } else if ("red255" in polyParam) {
        rgb255Create(color, polyParam)
    } else {
        linearCreate(color, polyParam)
    }
}


function hslCreate (color, polyParam) {
    if (color.perceptual) {
        let {hue, saturation = 0.9, lightness = 0.7} = polyParam
        color.hslValues      = {hue, saturation, lightness}
    } else {
        classicHslCreate(color, polyParam)
    }
}


function classicHslCreate (color, {hue, saturation = 1, lightness = 0.5}) {
    if (hue > 1 || hue < 0) {
        hue -= Math.floor(hue)
    }
    saturation = clamp(saturation)
    lightness  = clamp(lightness)
    color.hslValues = {hue, saturation, lightness}
}


function lightCreate (color, light) {
    color.rgbValues = grayRGB(light)
}


function rgb255Create (color, {red255, green255, blue255}) {
    color.rgbValues = {
        red:   red255   / 255,
        green: green255 / 255,
        blue:  blue255  / 255
    }
}


function cssCreate (color, param) {
    const {red, green, blue, opacity} = convertCSSToRGB(param)
    color.rgbValues = {red, green, blue}
    color.opacity = opacity
    color.perceptual = true
}


function linearCreate (color, polyParam) {
    if ("lightLinear" in polyParam) {
        const light = convertToSRGB(polyParam.lightLinear)
        lightCreate(color, light)
    } else if ("redLinear" in polyParam) {
        color.rgbValues = {
            red:   convertToSRGB(polyParam.redLinear),
            green: convertToSRGB(polyParam.greenLinear),
            blue:  convertToSRGB(polyParam.blueLinear)
        }
    } else {
        throw new Error("Invalid parameters used to create a color")
    }
}



//********************* CLASS CONVERSION METHODS


Color.createFromOklab = function (params) {
    return new Color(convertOkLabToRGB(params))
}


Color.createFromOkHSL = function (params) {
    return new Color(convertOkHSLToRGB(params))
}


Color.createFromOkHSV = function (params) {
    return new Color(convertOkHSVToRGB(params))
}



Color.autoConvertToRGB = autoConvertToRGB

Color.convertHexToRGB  = convertHexToRGB

Color.convertCSSToRGB  = convertCSSToRGB


Color.autoConvertToRGB255 = autoConvertToRGB255

Color.convertTo255       = convertTo255


Color.convertToSRGB      = convertToSRGB

Color.convertRGBToHSL   = convertRGBToHSL
Color.convertHSLToRGB   = convertHSLToRGB

Color.convertRGBToOkLab = convertRGBToOkLab
Color.convertOkLabToRGB = convertOkLabToRGB

Color.convertOkHSLToRGB = convertOkHSLToRGB
Color.convertRGBToOkHSL = convertRGBToOkHSL

Color.convertOkHSVToRGB = convertOkHSVToRGB
Color.convertRGBToOkHSV = convertRGBToOkHSV








//********************* CLASS TOOLS

Color.isCSSColor = function (text) {
    try {
        convertCSSToRGB(text)
    } catch (e) {
        return false
    }
    return true
}


Color.modifyHSLOfRGB255 = function (red255, green255, blue255, paramsOrModifier = {}) {
    const hsl = convertRGB255ToHSL(red255, green255, blue255)
    if (typeof paramsOrModifier === "function") {
        const modified = paramsOrModifier(hsl.hue, hsl.saturation, hsl.lightness)
        return convertHSLToRGB255NoClamp({hue: modified[0], saturtion: modified[1], lightness: modified[2]})
    } else {
        modifyHSL(hsl, paramsOrModifier)
        return convertHSLToRGB255NoClamp(hsl)
    }
}





//********************* INSTANCE METHODS - conversions and different color spaces



Object.defineProperty(Color.prototype, "hue", {
    enumerable: true,
    get: function () {
        return this.getHSL().hue
    },
    set: function (value) {
        this.getHSL()
        if (value > 1 || value < 0) {
            value -= Math.floor(value)
        }
        this.hslValues.hue = value
        delete this.rgbValues
    }
})

Object.defineProperty(Color.prototype, "saturation", {
    enumerable: true,
    get: function () {
        return this.getHSL().saturation
    },
    set: function (value) {
        this.getHSL()
        this.hslValues.saturation = clamp(value)
        delete this.rgbValues
    }
})

Object.defineProperty(Color.prototype, "lightness", {
    enumerable: true,
    get: function () {
        return this.getHSL().lightness
    },
    set: function (value) {
        this.getHSL()
        this.hslValues.lightness = clamp(value)
        delete this.rgbValues
    }
})



Object.defineProperty(Color.prototype, "luminance", {
    enumerable: true,
    get: function () {
        return linearLuminance(this)
    }
})


// CIELab 1976
Object.defineProperty(Color.prototype, "perceptualLightness", {
    enumerable: true,
    get: function () {
        const luminance = linearLuminance(this)
        if (luminance <= 0.008856) {
            return luminance * 903.3
        } else {
            return Math.pow(luminance, 0.333) * 116 - 16
        }
    }
})



Object.defineProperty(Color.prototype, "red", {
    enumerable: true,
    get: function () {
        return this.getRGB().red
    },
    set: function (value) {
        this.getRGB()
        this.rgbValues.red = value
        delete this.hslValues
    }
})

Object.defineProperty(Color.prototype, "green", {
    enumerable: true,
    get: function () {
        return this.getRGB().green
    },
    set: function (value) {
        this.getRGB()
        this.rgbValues.green = value
        delete this.hslValues
    }
})

Object.defineProperty(Color.prototype, "blue", {
    enumerable: true,
    get: function () {
        return this.getRGB().blue
    },
    set: function (value) {
        this.getRGB()
        this.rgbValues.blue = value
        delete this.hslValues
    }
})



Object.defineProperty(Color.prototype, "red255", {
    enumerable: true,
    get: function () {
        return convertTo255(this.getRGB().red)
    }
})

Object.defineProperty(Color.prototype, "green255", {
    enumerable: true,
    get: function () {
        return convertTo255(this.getRGB().green)
    }
})

Object.defineProperty(Color.prototype, "blue255", {
    enumerable: true,
    get: function () {
        return convertTo255(this.getRGB().blue)
    }
})




Object.defineProperty(Color.prototype, "redLinear", {
    enumerable: true,
    get: function () {
        return convertToLinear(this.getRGB().red)
    }
})

Object.defineProperty(Color.prototype, "greenLinear", {
    enumerable: true,
    get: function () {
        return convertToLinear(this.getRGB().green)
    }
})

Object.defineProperty(Color.prototype, "blueLinear", {
    enumerable: true,
    get: function () {
        return convertToLinear(this.getRGB().blue)
    }
})


Color.prototype.toOkLab = function () {
    return convertRGBToOkLab(this.getRGB())
}


// D65
Color.prototype.toXyz = function () {
    return convertRGBToXYZ(this.getRGB())
}


Color.prototype.toString = function () {
    let {red, green, blue} = this.getRGB()
    if (this.opacity === 1) {
        return `rgb(${
            convertTo255(red)} ${
            convertTo255(green)} ${
            convertTo255(blue)})`
    } else {
        return `rgb(${
            convertTo255(red)} ${
            convertTo255(green)} ${
            convertTo255(blue)} / ${
            this.opacity})`
    }
}

Color.prototype.toCSSString = Color.prototype.toString


// 8 digits only when the color is not fully opaque, so the common case stays #rrggbb
Color.prototype.toHex = function () {
    let {red, green, blue} = this.getRGB()
    let hex = `#${hexChannel(red)}${hexChannel(green)}${hexChannel(blue)}`

    return this.opacity === 1 ? hex : `${hex}${hexChannel(this.opacity)}`
}


function hexChannel (value) {
    return convertTo255(clamp(value)).toString(16).padStart(2, "0")
}


// how log(color) shows in terminals (rich_value.js): a swatch + the css
// string + readably rounded channel values
Color.prototype.terminalDisplay = function () {
    const css       = this.toString()
    const displayed = {constructorName: "Color", css}

    const values = this.rgbValues || this.hslValues
    for (let key in values) {
        displayed[key] = roundChannel(values[key])
    }
    if (this.opacity !== 1) {
        displayed.opacity = roundChannel(this.opacity)
    }

    displayed.thumbnail = () => colorSwatchCanvas(css)
    return displayed
}


function roundChannel (value) {
    return Math.round(value * 1000) / 1000
}


const swatchSize = 13

// only ever called from a terminal display, so from a browser context
function colorSwatchCanvas (css) {
    const canvas  = document.createElement("canvas") //eslint-disable-line no-undef
    canvas.width  = swatchSize
    canvas.height = swatchSize
    const cx      = canvas.getContext("2d")
    cx.fillStyle  = css
    cx.fillRect(0, 0, swatchSize, swatchSize)
    return canvas
}


Color.prototype.getRGB = function () {
    if (!this.rgbValues) {
        if (this.perceptual) {
            let {red, green, blue} = convertOkHSLToRGB(this.hslValues)
            this.rgbValues = {red, green, blue}
        } else {
            this.rgbValues = convertHSLToRGBNoClamp(this.hslValues)
        }
    }
    const {red, green, blue} = this.rgbValues
    return {red, green, blue}
}

Color.prototype.getHSL = function () {
    if (!this.hslValues) {
        if (this.perceptual) {
            let {hue, saturation, lightness} = convertRGBToOkHSL(this.rgbValues)
            this.hslValues = {hue, saturation, lightness}
        } else {
            this.hslValues = convertRGBToHSL(this.rgbValues)
        }
    }
    const {hue, saturation, lightness} = this.hslValues
    return {hue, saturation, lightness}
}





//********************* INSTANCE METHODS


Color.prototype.darken = function (value) {
    this.lightness *= (1 - value)
    return this
}

Color.prototype.lighten = function (value) {
    let {lightness} = this
    this.lightness = lightness + (1 - lightness) * value
    return this
}


Color.prototype.saturate = function (value) {
    let {saturation} = this
    this.saturation = saturation + (1 - saturation) * value
    return this
}

Color.prototype.desaturate = function (value) {
    this.saturation *= (1 - value)
    return this
}


Color.prototype.fadeIn = function (value) {
    this.opacity = clamp(this.opacity + (1 - this.opacity) * value)
    return this
}

Color.prototype.fadeOut = function (value) {
    this.opacity = clamp(this.opacity * (1 - value))
    return this
}


//FIXME remove soon
Color.prototype.modifyHSL = function (params = {}) {
    console.error(
        "modifyHSL is deprecated. Modify color.hue, color.saturation, color.lightness directly" +
        " or use darken, lighten, saturate, desaturate methods"
    )
    const hsla = this.getHSL()
    hsla.perceptual = this.perceptual
    modifyHSL(hsla, params)
    hsla.opacity = getOpacityFromParams(this.opacity, params)
    return new Color(hsla)
}

Color.prototype.modifyHSLA = Color.prototype.modifyHSL //FIXME remove soon


Color.prototype.writeOnItWithDark = function () {
    return this.perceptualLightness > 50
}


Color.prototype.clone = function () {
    let newColor
    if ("rgbValues" in this) {
        newColor = new Color(this.getRGB())
        newColor.perceptual = this.perceptual
    } else {
        let hslValues = this.getHSL()
        hslValues.perceptual = this.perceptual
        newColor = new Color(hslValues)
    }

    newColor.opacity = this.opacity

    return newColor
}



//FIXME optimize (cache)

Color.prototype.distanceTo = function (otherColor) {
    if (typeof otherColor === "string") {
        otherColor = new Color(otherColor)
    }
    const okLab      = Color.convertRGBToOkLab(this.getRGB())
    const otherOkLab = Color.convertRGBToOkLab(otherColor.getRGB())
    return distance(okLab, otherOkLab)
}




// this is lab D65
function rgbToCIELab (rgbParams) {
    return xyzToCIELab(convertRGBToXYZ(rgbParams))
}

// D50
function rgbToCIELabD50 (rgbParams) {
    return xyzToCIELabD50(convertRGBToXYZD50(rgbParams))
}


// D65
function xyzToCIELab ({x, y, z}) {

    x /= 0.95047
    z /= 1.08883

    x = (x > 0.008856) ? Math.pow(x, 1 / 3) : (903.3 * x + 16) / 116
    y = (y > 0.008856) ? Math.pow(y, 1 / 3) : (903.3 * y + 16) / 116
    z = (z > 0.008856) ? Math.pow(z, 1 / 3) : (903.3 * z + 16) / 116

    return {
        L: (116 * y) - 16,
        a: 500 * (x - y),
        b: 200 * (y - z)
    }
}

// D50
function xyzToCIELabD50 ({x, y, z}) {

    x /= 0.9642
    z /= 0.8249

    x = (x > 0.008856) ? Math.pow(x, 1 / 3) : (903.3 * x + 16) / 116
    y = (y > 0.008856) ? Math.pow(y, 1 / 3) : (903.3 * y + 16) / 116
    z = (z > 0.008856) ? Math.pow(z, 1 / 3) : (903.3 * z + 16) / 116

    return {
        L: (116 * y) - 16,
        a: 500 * (x - y),
        b: 200 * (y - z)
    }
}


Color.prototype.deltaE2000 = function (otherColor) {
    if (typeof otherColor === "string") {
        otherColor = new Color(otherColor)
    }
    const cieLab      = this.toCIElab()
    const otherCieLab = otherColor.toCIElab()
    return deltaE2000(cieLab, otherCieLab)
}


Color.prototype.toCIElab = function () {
    return rgbToCIELab(this.getRGB())
}


Color.prototype.toCIElabD50 = function () {
    return rgbToCIELabD50(this.getRGB())
}



function trueModulo (value, modulo) {
    return (value % modulo + modulo) % modulo
}

// Prefer using .distanceTo(other) < threshold directly for more explicit control
Color.prototype.isCloseTo = function (otherColor, threshold = 0.2) {
    return this.distanceTo(otherColor) < threshold
}


Color.prototype.equals = function (otherColor) {
    if (!(otherColor instanceof Color)) {
        otherColor = new Color(otherColor)
    }

    let {red, green, blue} = this.getRGB()
    return (
        red   === otherColor.red   &&
        green === otherColor.green &&
        blue  === otherColor.blue  &&
        this.opacity === otherColor.opacity
    )
}



//********************* Conversion tools

function autoConvertToRGB (polyParam, green, blue) {
    const type = typeof polyParam
    if (type === "object") {
        return autoConvertToRGBFromObject(polyParam)
    } else if (type === "string") {
        return convertCSSToRGB(polyParam)
    } else {
        return {red: polyParam, green, blue}
    }
}


function autoConvertToRGBFromObject (polyParam) { //eslint-disable-line complexity
    if ("red" in polyParam) {
        return polyParam
    } else if ("hue" in polyParam) {
        if (polyParam.perceptual) {
            return convertOkHSLToRGB(polyParam)
        } else {
            return convertHSLToRGB(polyParam)
        }
    } else if ("red255" in polyParam) {
        return {
            red:   polyParam.red255   / 255,
            green: polyParam.green255 / 255,
            blue:  polyParam.blue255  / 255
        }
    } else if ("light" in polyParam) {
        return grayRGB(polyParam.light)
    } else if ("lightLinear" in polyParam) {
        return grayRGB(convertToSRGB(polyParam.lightLinear))
    } else if ("redLinear" in polyParam) {
        return convertLinearsToRGB(polyParam)
    }
    return null
}



// opacity is resolved here, in a single place: CSS strings carry their own alpha, every other
// format reads it from the param object. The channel converters stay opacity-free so an internal
// default (e.g. okHSL's opacity = 1) can never leak into the result.
function autoConvertToRGB255 (polyParam, green, blue) {
    const type = typeof polyParam
    if (type === "string") {
        return convertCSSToRGB255(polyParam)
    }
    if (type !== "object") {
        return convertRGBToRGB255({red: polyParam, green, blue})
    }
    const rgb255 = convertColorObjectToRGB255(polyParam)
    if (polyParam.opacity !== undefined) {
        rgb255.opacity = convertTo255(polyParam.opacity)
    }
    return rgb255
}


function convertColorObjectToRGB255 (polyParam) { //eslint-disable-line complexity
    if ("red" in polyParam) {
        return convertRGBToRGB255(polyParam)
    } else if ("red255" in polyParam) {
        return {
            red:   polyParam.red255,
            green: polyParam.green255,
            blue:  polyParam.blue255
        }
    } else if ("hue" in polyParam) {
        return convertHSLOrOkHSLToRGB255(polyParam)
    } else if ("light" in polyParam) {
        return grayRGB255(polyParam.light)
    } else if ("lightLinear" in polyParam) {
        return grayRGB255(convertToSRGB(polyParam.lightLinear))
    } else if ("redLinear" in polyParam) {
        return convertLinearsToRGB255(polyParam)
    }
    return convertRGBToRGB255({red: polyParam})
}



function grayRGB (value) {
    return {
        red:   value,
        green: value,
        blue:  value
    }
}


function grayRGB255 (value) {
    value = convertTo255(value)
    return {
        red:   value,
        green: value,
        blue:  value
    }
}

function convertLinearsToRGB (params) {
    return {
        red:   convertToSRGB(params.redLinear),
        green: convertToSRGB(params.greenLinear),
        blue:  convertToSRGB(params.blueLinear)
    }
}


function convertLinearsToRGB255 (params) {
    return {
        red:   convertToSRGB255(params.redLinear),
        green: convertToSRGB255(params.greenLinear),
        blue:  convertToSRGB255(params.blueLinear)
    }
}



function convertRGB255ToHSL (red, green, blue) {
    red   /= 255
    green /= 255
    blue  /= 255
    return convertRGBToHSL({red: red / 255, green: green / 255, blue: blue / 255})
}


function convertRGBToHSL ({red, green, blue}) {
    const max = Math.max(red, green, blue)
    const min = Math.min(red, green, blue)
    const lightness = (max + min) / 2
    const chroma = max - min

    if (chroma === 0) {
        return {hue: 0, saturation: 0, lightness}
    } else {
        const saturation = getSaturation(lightness, chroma, min, max)
        const hue        = getHue(max, red, green, blue, chroma)
        return {hue, saturation, lightness}
    }
}



function getSaturation (l, chroma, min, max) {
    if (l > 0.5) {
        return chroma / (2 - max - min)
    } else {
        return chroma / (max + min)
    }
}


function getHue (max, r, g, b, chroma) {
    if (max === b) {
        return (4 + (r - g) / chroma) / 6
    }
    if (max === g) {
        return (2 + (b - r) / chroma) / 6
    }
    if (g < b) {
        return 1 + (g - b) / (6 * chroma)
    }
    return (g - b) / (6 * chroma)
}






function convertHSLToRGB ({hue, saturation = 1, lightness = 0.5}) {
    if (hue > 1 || hue < 0) {
        hue -= Math.floor(hue)
    }
    saturation = clamp(saturation)
    lightness  = clamp(lightness)
    const q = qParam(lightness, saturation)
    const p = 2 * lightness - q
    hue *= 6
    return {
        red:   hueToRGB(p, q, hue + 2),
        green: hueToRGB(p, q, hue),
        blue:  hueToRGB(p, q, hue - 2)
    }
}


function convertHSLOrOkHSLToRGB255 ({hue, saturation = 1, lightness = 0.5, perceptual = true}) {
    if (hue > 1 || hue < 0) {
        hue -= Math.floor(hue)
    }
    saturation = clamp(saturation)
    lightness  = clamp(lightness)
    if (perceptual) {
        return convertRGBToRGB255(convertOkHSLToRGB({hue, saturation, lightness}))
    } else {
        const q = qParam(lightness, saturation)
        const p = 2 * lightness - q
        hue *= 6
        return {
            red:   convertTo255(hueToRGB(p, q, hue + 2)),
            green: convertTo255(hueToRGB(p, q, hue)),
            blue:  convertTo255(hueToRGB(p, q, hue - 2))
        }
    }
}


function convertHSLToRGBNoClamp ({hue, saturation, lightness}) {
    const q = qParam(lightness, saturation)
    const p = 2 * lightness - q
    hue *= 6
    return {
        red:   hueToRGB(p, q, hue + 2),
        green: hueToRGB(p, q, hue),
        blue:  hueToRGB(p, q, hue - 2)
    }
}


function convertHSLToRGB255NoClamp ({hue, saturation, lightness}) {
    const q = qParam(lightness, saturation)
    const p = 2 * lightness - q
    hue *= 6
    return {
        red:   convertTo255(hueToRGB(p, q, hue + 2)),
        green: convertTo255(hueToRGB(p, q, hue)),
        blue:  convertTo255(hueToRGB(p, q, hue - 2))
    }
}


function convertRGBToRGB255 ({red, green, blue}) {
    return {
        red:   convertTo255(red),
        green: convertTo255(green),
        blue:  convertTo255(blue)
    }
}


function convertCSSToRGB255 (cssColor) {
    const {red, green, blue, opacity} = convertCSSToRGB(cssColor)
    return {
        red:     convertTo255(red),
        green:   convertTo255(green),
        blue:    convertTo255(blue),
        opacity: convertTo255(opacity)
    }
}


function convertTo255 (value) {
    return Math.floor(255.999 * value)
}


//from our sRGB standard Gamma space value
function convertToLinear (valueWithGamma) {
    if (valueWithGamma <= 0.04045) {
        return valueWithGamma / 12.92
    } else {
        return Math.pow((valueWithGamma + 0.055) / 1.055, 2.4)
    }
}


const gammaPower = 1 / 2.4
function convertToSRGB (linearValue) {
    if (linearValue < 0) {
        return 0
    } else if (linearValue <= 0.0031308) {
        return linearValue * 12.92
    } else {
        return Math.min(1, 1.055 * Math.pow(linearValue, gammaPower) - 0.055)
    }
}


function convertToSRGB255 (linearValue) {
    return convertTo255(convertToSRGB(linearValue))
}


// sRGB Luminance  https://en.wikipedia.org/wiki/Relative_luminance
function linearLuminance (color) {
    let {red, green, blue} = color.getRGB()
    return (
        0.2126 * convertToLinear(red) +
        0.7152 * convertToLinear(green) +
        0.0722 * convertToLinear(blue)
    )
}



// D65 http://www.brucelindbloom.com/index.html?Eqn_RGB_XYZ_Matrix.html
function convertRGBToXYZ ({red, green, blue}) {
    red   = convertToLinear(red)
    green = convertToLinear(green)
    blue  = convertToLinear(blue)
    return {
        x: red * 0.4124108464885388   + green * 0.3575845678529519  + blue * 0.18045380393360833,
        y: red * 0.21264934272065283  + green * 0.7151691357059038  + blue * 0.07218152157344333,
        z: red * 0.019331758429150258 + green * 0.11919485595098397 + blue * 0.9503900340503373
    }
}

// D50
function convertRGBToXYZD50 ({red, green, blue}) {
    red   = convertToLinear(red)
    green = convertToLinear(green)
    blue  = convertToLinear(blue)
    return {
        x: red * 0.4360747 + green * 0.3850649 + blue * 0.1430804,
        y: red * 0.2225045 + green * 0.7168786 + blue * 0.0606169,
        z: red * 0.0139322 + green * 0.0971045 + blue * 0.7141733
    }
}










function qParam (l, s) {
    if (l < 0.5) {
        return l * (1 + s)
    } else {
        return l + s - l * s
    }
}

function hueToRGB (p, q, t) {
    if (t < 0) {
        t += 6
    } else if (t > 6) {
        t -= 6
    }

    if (t < 1) {
        return p + (q - p) * t
    }
    if (t < 3) {
        return q
    }
    if (t < 4) {
        return p + (q - p) * (4 - t)
    }
    return p
}



function convertCSSToRGB (hexOrColorName) {
    hexOrColorName = hexOrColorName.trim()
    if (hexOrColorName[0] === "#") {
        return convertHexToRGB(hexOrColorName)
    }

    if (hexOrColorName.startsWith("rgb")) {
        return convertCSSParenthesisSyntaxToRGB(hexOrColorName)
    }

    const rgb = Color.allCSSColors[hexOrColorName]

    if (rgb) {
        return {opacity: 1, ...rgb}
    }

    throw new Error(`color "${hexOrColorName}" not recognized`)
}




const cssRatio = 16 / 17

function convertHexToRGB (hex) {

    if (/^#[A-Fa-f0-9]{3}$/.test(hex)) {
        return {
            red:   hexCharToFloat[hex[1]],
            green: hexCharToFloat[hex[2]],
            blue:  hexCharToFloat[hex[3]],
            opacity: 1
        }
    } else if (/^#[A-Fa-f0-9]{4}$/.test(hex)) {
        return {
            red:   hexCharToFloat[hex[1]],
            green: hexCharToFloat[hex[2]],
            blue:  hexCharToFloat[hex[3]],
            opacity: hexCharToFloat[hex[4]]
        }
    } else if (/^#[A-Fa-f0-9]{6}$/.test(hex)) {
        return {
            red:   hexCharToFloat[hex[1]] * cssRatio + hexCharToFloat[hex[2]] / 17,
            green: hexCharToFloat[hex[3]] * cssRatio + hexCharToFloat[hex[4]] / 17,
            blue:  hexCharToFloat[hex[5]] * cssRatio + hexCharToFloat[hex[6]] / 17,
            opacity: 1
        }
    } else if (/^#[A-Fa-f0-9]{8}$/.test(hex)) {
        return {
            red:   hexCharToFloat[hex[1]] * cssRatio + hexCharToFloat[hex[2]] / 17,
            green: hexCharToFloat[hex[3]] * cssRatio + hexCharToFloat[hex[4]] / 17,
            blue:  hexCharToFloat[hex[5]] * cssRatio + hexCharToFloat[hex[6]] / 17,
            opacity: hexCharToFloat[hex[7]] * cssRatio + hexCharToFloat[hex[8]] / 17
        }
    } else {
        throw new Error(`color "${hex}" not recognized`)
    }
}




function convertCSSParenthesisSyntaxToRGB (value) {
    const inside = value.match(colorInCssParenthesisSimpleRegex)[1]
    const components = splitCSSColorComponents(inside)
    if (!components || components.length < 3 || components.length > 4) {
        throw new Error(`color "${value}" not recognized`)
    }
    const [r, g, b, o = "1"] = components
    return {
        red:     parseInt(r, 10) / 255,
        green:   parseInt(g, 10) / 255,
        blue:    parseInt(b, 10) / 255,
        opacity: o.endsWith("%") ? parseFloat(o, 10) / 100 : parseFloat(o, 10)
    }
}


function splitCSSColorComponents (inside) {
    if (inside.includes(",")) {
        return inside.split(",")
    } else {
        let matched = inside.match(/(\d+)\s+(\d+)\s+(\d+)(?:\s*\/\s*([\d.]+%?))?$/)
        if (!matched) {
            return false
        }
        let [, r, g, b, a] = matched
        return [r, g, b, a]
    }
}


const colorInCssParenthesisSimpleRegex = /^rgba?\((.*)\)$/



//********************* MISC


function clamp (value) {
    return Math.max(Math.min(value, 1), 0)
}


function getSaturationFromParams (saturation, params) {
    if ("saturation" in params) {
        return clamp(params.saturation)
    }

    if ("desaturate" in params) {
        return clamp(saturation * (1 - params.desaturate))
    }

    if ("saturate" in params) {
        return clamp(saturation + (1 - saturation) * params.saturate)
    }

    return saturation
}



function getLightnessFromParams (lightness, params) {
    if ("lightness" in params) {
        return clamp(params.lightness)
    }

    if ("darken" in params) {
        return clamp(lightness * (1 - params.darken))
    }

    if ("lighten" in params) {
        return clamp(lightness + (1 - lightness) * params.lighten)
    }

    return lightness
}


function getOpacityFromParams (opacity, params) {
    if ("opacity" in params) {
        return clamp(params.opacity)
    }

    if ("fadeOut" in params) {
        return clamp(opacity * (1 - params.fadeOut))
    }

    if ("fadeIn" in params) {
        return clamp(opacity + (1 - opacity) * params.fadeIn)
    }

    return opacity
}



function modifyHSL (hsl, params) {
    hsl.saturation = getSaturationFromParams(hsl.saturation, params)
    hsl.lightness  = getLightnessFromParams(hsl.lightness, params)
}







// https://iquilezles.org/www/articles/palettes/palettes.htm

const cosinePalettes = {
    a: {
        phase: [0.0, 0.33, 0.67]
    },
    dark: {
        phase: [0.55, 0.45, 0.45]
    },
    dark2: {
        frequency: [0.7, 0.7, 0.7],
        phase:     [0.55, 0.45, 0.45]
    }
}


const twoPI = 2 * Math.PI

Color.getColorMapper = function ({
    colorCount = 300,
    palette: paletteName = "dark2",
    cycle = false,
    useIndexes = false,
    outputAsRGB255 = true
} = {}) {

    let palette = []

    let {
        zero      = [0.5, 0.5, 0.5],
        amplitude = [0.5, 0.5, 0.5],
        frequency = [1.0, 1.0, 1.0],
        phase
    } = cosinePalettes[paletteName]

    function cosColor (t, i) {
        return zero[i] + amplitude[i] * Math.cos(twoPI * (frequency[i] * t + phase[i]))
    }

    function cosColor255 (t, i) {
        return convertTo255(cosColor(t, i))
    }


    const colorCountMinusOne = colorCount - 1

    for (let i = 0; i < colorCount; i++) {
        let t = i / colorCountMinusOne

        if (outputAsRGB255) {
            palette.push({
                red255:   cosColor255(t, 0),
                green255: cosColor255(t, 1),
                blue255:  cosColor255(t, 2)
            })
        } else {
            palette.push({
                red:   cosColor(t, 0),
                green: cosColor(t, 1),
                blue:  cosColor(t, 2)
            })
        }
    }


    if (cycle) {
        if (useIndexes) {
            return (value) => {
                return palette[trueModulo(value, colorCount) | 0]
            }
        } else {
            return (value) => {
                if (value >= 1 || value < 0) {
                    value -= Math.floor(value)
                }
                return palette[colorCount * value | 0]
            }
        }
    }

    if (useIndexes) {
        return (value) => palette[Math.max(0, Math.min(value | 0, colorCountMinusOne))]
    } else {
        return (value) => palette[colorCount * Math.max(0, Math.min(value, 0.99999)) | 0]
    }
}















Color.allCSSColors = {
    aliceblue:            {red: 240 / 255, green: 248 / 255, blue: 1},
    antiquewhite:         {red: 250 / 255, green: 235 / 255, blue: 215 / 255},
    aqua:                 {red: 0, green: 1, blue: 1},
    aquamarine:           {red: 127 / 255, green: 1, blue: 212 / 255},
    azure:                {red: 240 / 255, green: 1, blue: 1},
    beige:                {red: 245 / 255, green: 245 / 255, blue: 220 / 255},
    bisque:               {red: 1, green: 228 / 255, blue: 196 / 255},
    black:                {red: 0, green: 0, blue: 0},
    blanchedalmond:       {red: 1, green: 235 / 255, blue: 205 / 255},
    blue:                 {red: 0, green: 0, blue: 1},
    blueviolet:           {red: 138 / 255, green: 43 / 255, blue: 226 / 255},
    brown:                {red: 165 / 255, green: 42 / 255, blue: 42 / 255},
    burlywood:            {red: 222 / 255, green: 184 / 255, blue: 135 / 255},
    cadetblue:            {red: 95 / 255, green: 158 / 255, blue: 160 / 255},
    chartreuse:           {red: 127 / 255, green: 1, blue: 0},
    chocolate:            {red: 210 / 255, green: 105 / 255, blue: 30 / 255},
    coral:                {red: 1, green: 127 / 255, blue: 80 / 255},
    cornflowerblue:       {red: 100 / 255, green: 149 / 255, blue: 237 / 255},
    cornsilk:             {red: 1, green: 248 / 255, blue: 220 / 255},
    crimson:              {red: 220 / 255, green: 20 / 255, blue: 60 / 255},
    cyan:                 {red: 0, green: 1, blue: 1},
    darkblue:             {red: 0, green: 0, blue: 139 / 255},
    darkcyan:             {red: 0, green: 139 / 255, blue: 139 / 255},
    darkgoldenrod:        {red: 184 / 255, green: 134 / 255, blue: 11 / 255},
    darkgray:             {red: 169 / 255, green: 169 / 255, blue: 169 / 255},
    darkgreen:            {red: 0, green: 100 / 255, blue: 0},
    darkgrey:             {red: 169 / 255, green: 169 / 255, blue: 169 / 255},
    darkkhaki:            {red: 189 / 255, green: 183 / 255, blue: 107 / 255},
    darkmagenta:          {red: 139 / 255, green: 0, blue: 139 / 255},
    darkolivegreen:       {red: 85 / 255, green: 107 / 255, blue: 47 / 255},
    darkorange:           {red: 1, green: 140 / 255, blue: 0},
    darkorchid:           {red: 153 / 255, green: 50 / 255, blue: 204 / 255},
    darkred:              {red: 139 / 255, green: 0, blue: 0},
    darksalmon:           {red: 233 / 255, green: 150 / 255, blue: 122 / 255},
    darkseagreen:         {red: 143 / 255, green: 188 / 255, blue: 143 / 255},
    darkslateblue:        {red: 72 / 255, green: 61 / 255, blue: 139 / 255},
    darkslategray:        {red: 47 / 255, green: 79 / 255, blue: 79 / 255},
    darkslategrey:        {red: 47 / 255, green: 79 / 255, blue: 79 / 255},
    darkturquoise:        {red: 0, green: 206 / 255, blue: 209 / 255},
    darkviolet:           {red: 148 / 255, green: 0, blue: 211 / 255},
    deeppink:             {red: 1, green: 20 / 255, blue: 147 / 255},
    deepskyblue:          {red: 0, green: 191 / 255, blue: 1},
    dimgray:              {red: 105 / 255, green: 105 / 255, blue: 105 / 255},
    dimgrey:              {red: 105 / 255, green: 105 / 255, blue: 105 / 255},
    dodgerblue:           {red: 30 / 255, green: 144 / 255, blue: 1},
    firebrick:            {red: 178 / 255, green: 34 / 255, blue: 34 / 255},
    floralwhite:          {red: 1, green: 250 / 255, blue: 240 / 255},
    forestgreen:          {red: 34 / 255, green: 139 / 255, blue: 34 / 255},
    fuchsia:              {red: 1, green: 0, blue: 1},
    gainsboro:            {red: 220 / 255, green: 220 / 255, blue: 220 / 255},
    ghostwhite:           {red: 248 / 255, green: 248 / 255, blue: 1},
    gold:                 {red: 1, green: 215 / 255, blue: 0},
    goldenrod:            {red: 218 / 255, green: 165 / 255, blue: 32 / 255},
    gray:                 {red: 128 / 255, green: 128 / 255, blue: 128 / 255},
    green:                {red: 0, green: 128 / 255, blue: 0},
    greenyellow:          {red: 173 / 255, green: 1, blue: 47 / 255},
    grey:                 {red: 128 / 255, green: 128 / 255, blue: 128 / 255},
    honeydew:             {red: 240 / 255, green: 1, blue: 240 / 255},
    hotpink:              {red: 1, green: 105 / 255, blue: 180 / 255},
    indianred:            {red: 205 / 255, green: 92 / 255, blue: 92 / 255},
    indigo:               {red: 75 / 255, green: 0, blue: 130 / 255},
    ivory:                {red: 1, green: 1, blue: 240 / 255},
    khaki:                {red: 240 / 255, green: 230 / 255, blue: 140 / 255},
    lavender:             {red: 230 / 255, green: 230 / 255, blue: 250 / 255},
    lavenderblush:        {red: 1, green: 240 / 255, blue: 245 / 255},
    lawngreen:            {red: 124 / 255, green: 252 / 255, blue: 0},
    lemonchiffon:         {red: 1, green: 250 / 255, blue: 205 / 255},
    lightblue:            {red: 173 / 255, green: 216 / 255, blue: 230 / 255},
    lightcoral:           {red: 240 / 255, green: 128 / 255, blue: 128 / 255},
    lightcyan:            {red: 224 / 255, green: 1, blue: 1},
    lightgoldenrodyellow: {red: 250 / 255, green: 250 / 255, blue: 210 / 255},
    lightgray:            {red: 211 / 255, green: 211 / 255, blue: 211 / 255},
    lightgreen:           {red: 144 / 255, green: 238 / 255, blue: 144 / 255},
    lightgrey:            {red: 211 / 255, green: 211 / 255, blue: 211 / 255},
    lightpink:            {red: 1, green: 182 / 255, blue: 193 / 255},
    lightsalmon:          {red: 1, green: 160 / 255, blue: 122 / 255},
    lightseagreen:        {red: 32 / 255, green: 178 / 255, blue: 170 / 255},
    lightskyblue:         {red: 135 / 255, green: 206 / 255, blue: 250 / 255},
    lightslategray:       {red: 119 / 255, green: 136 / 255, blue: 153 / 255},
    lightslategrey:       {red: 119 / 255, green: 136 / 255, blue: 153 / 255},
    lightsteelblue:       {red: 176 / 255, green: 196 / 255, blue: 222 / 255},
    lightyellow:          {red: 1, green: 1, blue: 224 / 255},
    lime:                 {red: 0, green: 1, blue: 0},
    limegreen:            {red: 50 / 255, green: 205 / 255, blue: 50 / 255},
    linen:                {red: 250 / 255, green: 240 / 255, blue: 230 / 255},
    magenta:              {red: 1, green: 0, blue: 1},
    maroon:               {red: 128 / 255, green: 0, blue: 0},
    mediumaquamarine:     {red: 102 / 255, green: 205 / 255, blue: 170 / 255},
    mediumblue:           {red: 0, green: 0, blue: 205 / 255},
    mediumorchid:         {red: 186 / 255, green: 85 / 255, blue: 211 / 255},
    mediumpurple:         {red: 147 / 255, green: 112 / 255, blue: 219 / 255},
    mediumseagreen:       {red: 60 / 255, green: 179 / 255, blue: 113 / 255},
    mediumslateblue:      {red: 123 / 255, green: 104 / 255, blue: 238 / 255},
    mediumspringgreen:    {red: 0, green: 250 / 255, blue: 154 / 255},
    mediumturquoise:      {red: 72 / 255, green: 209 / 255, blue: 204 / 255},
    mediumvioletred:      {red: 199 / 255, green: 21 / 255, blue: 133 / 255},
    midnightblue:         {red: 25 / 255, green: 25 / 255, blue: 112 / 255},
    mintcream:            {red: 245 / 255, green: 1, blue: 250 / 255},
    mistyrose:            {red: 1, green: 228 / 255, blue: 225 / 255},
    moccasin:             {red: 1, green: 228 / 255, blue: 181 / 255},
    navajowhite:          {red: 1, green: 222 / 255, blue: 173 / 255},
    navy:                 {red: 0, green: 0, blue: 128 / 255},
    oldlace:              {red: 253 / 255, green: 245 / 255, blue: 230 / 255},
    olive:                {red: 128 / 255, green: 128 / 255, blue: 0},
    olivedrab:            {red: 107 / 255, green: 142 / 255, blue: 35 / 255},
    orange:               {red: 1, green: 165 / 255, blue: 0},
    orangered:            {red: 1, green: 69 / 255, blue: 0},
    orchid:               {red: 218 / 255, green: 112 / 255, blue: 214 / 255},
    palegoldenrod:        {red: 238 / 255, green: 232 / 255, blue: 170 / 255},
    palegreen:            {red: 152 / 255, green: 251 / 255, blue: 152 / 255},
    paleturquoise:        {red: 175 / 255, green: 238 / 255, blue: 238 / 255},
    palevioletred:        {red: 219 / 255, green: 112 / 255, blue: 147 / 255},
    papayawhip:           {red: 1, green: 239 / 255, blue: 213 / 255},
    peachpuff:            {red: 1, green: 218 / 255, blue: 185 / 255},
    peru:                 {red: 205 / 255, green: 133 / 255, blue: 63 / 255},
    pink:                 {red: 1, green: 192 / 255, blue: 203 / 255},
    plum:                 {red: 221 / 255, green: 160 / 255, blue: 221 / 255},
    powderblue:           {red: 176 / 255, green: 224 / 255, blue: 230 / 255},
    purple:               {red: 128 / 255, green: 0, blue: 128 / 255},
    rebeccapurple:        {red: 102 / 255, green: 51 / 255, blue: 153 / 255},
    red:                  {red: 1, green: 0, blue: 0},
    rosybrown:            {red: 188 / 255, green: 143 / 255, blue: 143 / 255},
    royalblue:            {red: 65 / 255, green: 105 / 255, blue: 225 / 255},
    saddlebrown:          {red: 139 / 255, green: 69 / 255, blue: 19 / 255},
    salmon:               {red: 250 / 255, green: 128 / 255, blue: 114 / 255},
    sandybrown:           {red: 244 / 255, green: 164 / 255, blue: 96 / 255},
    seagreen:             {red: 46 / 255, green: 139 / 255, blue: 87 / 255},
    seashell:             {red: 1, green: 245 / 255, blue: 238 / 255},
    sienna:               {red: 160 / 255, green: 82 / 255, blue: 45 / 255},
    silver:               {red: 192 / 255, green: 192 / 255, blue: 192 / 255},
    skyblue:              {red: 135 / 255, green: 206 / 255, blue: 235 / 255},
    slateblue:            {red: 106 / 255, green: 90 / 255, blue: 205 / 255},
    slategray:            {red: 112 / 255, green: 128 / 255, blue: 144 / 255},
    slategrey:            {red: 112 / 255, green: 128 / 255, blue: 144 / 255},
    snow:                 {red: 1, green: 250 / 255, blue: 250 / 255},
    springgreen:          {red: 0, green: 1, blue: 127 / 255},
    steelblue:            {red: 70 / 255, green: 130 / 255, blue: 180 / 255},
    tan:                  {red: 210 / 255, green: 180 / 255, blue: 140 / 255},
    teal:                 {red: 0, green: 128 / 255, blue: 128 / 255},
    thistle:              {red: 216 / 255, green: 191 / 255, blue: 216 / 255},
    tomato:               {red: 1, green: 99 / 255, blue: 71 / 255},
    transparent:          {red: 0, green: 0, blue: 0, opacity: 0},
    turquoise:            {red: 64 / 255, green: 224 / 255, blue: 208 / 255},
    violet:               {red: 238 / 255, green: 130 / 255, blue: 238 / 255},
    wheat:                {red: 245 / 255, green: 222 / 255, blue: 179 / 255},
    white:                {red: 1, green: 1, blue: 1},
    whitesmoke:           {red: 245 / 255, green: 245 / 255, blue: 245 / 255},
    yellow:               {red: 1, green: 1, blue: 0},
    yellowgreen:          {red: 154 / 255, green: 205 / 255, blue: 50 / 255}
}



const hexCharToFloat = {
    0: 0,
    1: 1 / 15,
    2: 2 / 15,
    3: 0.2,
    4: 4 / 15,
    5: 5 / 15,
    6: 0.4,
    7: 7 / 15,
    8: 8 / 15,
    9: 0.6,
    a: 10 / 15,
    b: 11 / 15,
    c: 0.8,
    d: 13 / 15,
    e: 14 / 15,
    f: 1,
    A: 10 / 15,
    B: 11 / 15,
    C: 0.8,
    D: 13 / 15,
    E: 14 / 15,
    F: 1
}



export default Color

